import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PlanScenarioParams } from 'constants/routes';
import { useFeatureFlags } from 'contexts/FeatureFlags';
import { useSPMCollaborationContext } from 'contexts/SPMCollaborationContext';
import { useSPMPlanContext } from 'contexts/SPMPlanContext';
import { canViewCollaboratorResource } from 'hooks/canViewCollaboratorResource';
import { UsersService } from 'services';
import { SPMScenario } from 'types/SPM';
import { TQModel } from 'types/tqModels';

export type NavigationData = {
  activeScenario: SPMScenario;
  tqModel: TQModel;
  canViewQuotaModel: boolean;
  canViewCapacityModel: boolean;
  canViewTerritoriesModel: boolean;
};

export function useNavigationData(): NavigationData {
  const { scenarioId, planningCycleId } = useParams<PlanScenarioParams>();
  const { query: permissionsQuery } = UsersService.useGetCurrentUserPermissions();
  const userPermissions = permissionsQuery.data;
  const { isCollaborationUser } = useSPMCollaborationContext();
  const { isFeatureEnabled } = useFeatureFlags();
  const { scenarios, baseScenario, canViewQuotaModel, canViewCapacityModel } = useSPMPlanContext();
  const isPermissionsV2Enabled = isFeatureEnabled('feature_flag_spm_permissions_v2');

  const activeScenario = useMemo(() => {
    if (!scenarios) {
      return undefined;
    }
    return scenarios.find((sc) => sc.id === scenarioId) || scenarios[0] || baseScenario;
  }, [scenarios, scenarioId, baseScenario]);

  const activeScenarioId = scenarioId ?? activeScenario?.id;

  const canViewTerritoriesModel = useMemo(() => {
    if (!activeScenario) {
      return false;
    }
    return canViewCollaboratorResource({
      isCollaborationUser,
      userPermissions,
      planningCycleId,
      activeScenarioId,
      isPermissionsV2Enabled,
      modelId: activeScenario.hierarchy_model.id,
      modelType: 'territory',
    });
  }, [activeScenario, userPermissions, isCollaborationUser, activeScenarioId, planningCycleId, isPermissionsV2Enabled]);

  return useMemo(() => {
    if (!activeScenario) {
      return {} as NavigationData;
    }
    return {
      activeScenario,
      tqModel: activeScenario.hierarchy_model,
      canViewQuotaModel,
      canViewCapacityModel,
      canViewTerritoriesModel,
    };
  }, [activeScenario, canViewQuotaModel, canViewCapacityModel, canViewTerritoriesModel]);
}
